/**
 * WizardSteps - Individual step components for CreateTemplateWizard
 * Converted from TypeScript for PromptusAI PWA
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MediaRenderer, MediaTypeBadge, DefaultButtonSpinner, Icons, detectMediaType } from './MediaComponents';
import ModelGalleryPicker from '../ModelGalleryPicker';
import DynamicPromptForm from '../DynamicPromptForm';
import Swal from 'sweetalert2';

const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const SearchableUseCaseDropdown = ({
  value,
  onChange,
  options,
  disabled,
  placeholder = 'Select a use case',
  hasError = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(option => option.toLowerCase().includes(query));
  }, [options, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between gap-2 bg-dark-blue/50 border rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-cyan/50 transition-colors ${
          disabled ? 'opacity-60 cursor-not-allowed' : 'hover:border-white/40 text-white'
        } ${hasError ? 'border-red-400' : 'border-white/20'}`}
        disabled={disabled}
      >
        <span className={`truncate ${value ? 'text-white' : 'text-white/40'}`}>
          {value || placeholder}
        </span>
        <Icons.ChevronDown />
      </button>

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 mt-1 w-full bg-dark-blue border border-white/20 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
                <SearchIcon />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search use cases..."
                className="w-full pl-9 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-cyan/50 text-base"
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left text-base transition-colors ${
                    value === option
                      ? 'bg-yellow/20 text-yellow'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  <span>{option}</span>
                  {value === option && <Icons.Check />}
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-white/50 text-base text-center">
                No use cases found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// Step 1: Model & Prompt
// ============================================

export function Step1ModelPrompt({
  wizardData,
  setWizardData,
  errors,
  setErrors,
  models,
  isLoadingModels,
  isLoadingModel,
  isGenerating,
  onModelChange,
  onClearSubsequentSteps,
  ButtonSpinner = <DefaultButtonSpinner />,
}) {
  return (
    <div className="space-y-6">
      {/* Model Selection */}
      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Model / Cosyflow <span className="text-red-400">*</span>
        </label>
        <div className="flex gap-2 items-center">
          <select
            value={wizardData.model}
            onChange={(e) => {
              const selectedModel = e.target.value;
              const previousModel = wizardData.model;
              setWizardData(prev => ({ ...prev, model: selectedModel }));
              if (selectedModel) {
                onModelChange(selectedModel);
              }
              if (previousModel && previousModel !== selectedModel) {
                onClearSubsequentSteps();
              }
              if (errors.model) {
                setErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.model;
                  return newErrors;
                });
              }
            }}
            className={`
              flex-1 px-3 py-2.5 rounded-lg
              bg-dark-blue/50 border border-white/20
              text-white
              focus:outline-none focus:ring-2 focus:ring-cyan/50 focus:border-cyan
              ${errors.model ? 'border-red-400' : ''}
            `}
            disabled={isGenerating || isLoadingModels}
          >
            <option value="">{isLoadingModels ? 'Loading models...' : 'Select a model'}</option>
            {models.map((model) => (
              <option key={model.id} value={model.title || model.name}>
                {model.title || model.name}
              </option>
            ))}
          </select>
          {(isLoadingModel || isLoadingModels) && ButtonSpinner}
        </div>
        {errors.model && <p className="text-sm text-red-400 mt-1">{errors.model}</p>}
        {models.length === 0 && !isLoadingModels && (
          <p className="text-sm text-white/50 mt-1">No models available. Please check your connection.</p>
        )}
      </div>

      {/* Base Prompt */}
      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Base Prompt <span className="text-white/50 text-xs">(Optional)</span>
        </label>
        <textarea
          value={wizardData.basePrompt}
          onChange={(e) => {
            const newValue = e.target.value;
            const previousValue = wizardData.basePrompt;
            setWizardData(prev => ({ ...prev, basePrompt: newValue }));
            if (previousValue && previousValue !== newValue && wizardData.generatedItems.length > 0) {
              onClearSubsequentSteps();
            }
          }}
          placeholder="Enter base prompt that will be combined with all prompts..."
          rows={3}
          className="w-full px-3 py-2.5 rounded-lg resize-none bg-dark-blue/50 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan/50"
          disabled={isGenerating}
        />
        <p className="text-xs text-white/50 mt-1">This will be prepended to all generated prompts.</p>
      </div>
    </div>
  );
}

// ============================================
// Step 2: Gallery Generation
// ============================================

export function Step2GalleryGeneration({
  wizardData,
  setWizardData,
  errors,
  setErrors,
  imageFieldNames,
  isImageUploadExpanded,
  setIsImageUploadExpanded,
  isGenerating,
  generationProgress,
  modelOutputType,
  onGenerateContent,
  ButtonSpinner = <DefaultButtonSpinner />,
  selectedCosyflow,
}) {
  const fileInputRefs = useRef({});
  const dynamicFormRef = useRef(null);
  const [isGalleryPickerOpen, setIsGalleryPickerOpen] = useState(false);

  const handleValidatedGenerate = () => {
    if (dynamicFormRef.current?.validateRequiredFields) {
      const validation = dynamicFormRef.current.validateRequiredFields();
      if (!validation.valid) {
        const fieldNames = validation.errors.map(e => e.label).join(', ');
        Swal.fire({
          icon: 'error',
          title: 'Required Fields Missing',
          text: `Please fill in: ${fieldNames}`,
          confirmButtonColor: '#FFB02E',
          didOpen: () => {
            const container = document.querySelector('.swal2-container');
            if (container) container.style.zIndex = '99999';
          },
        });
        return;
      }
    }
    onGenerateContent();
  };
  const progressDisplay = Number.isFinite(generationProgress)
    ? Math.floor(generationProgress)
    : 0;

  const importance5Fields = useMemo(() => {
    console.log('[Step2] selectedCosyflow:', selectedCosyflow);
    console.log('[Step2] selectedCosyflow?.variables:', selectedCosyflow?.variables);
    if (!selectedCosyflow?.variables) {
      console.log('[Step2] No variables found, returning empty array');
      return [];
    }
    const fields = Object.entries(selectedCosyflow.variables)
      .filter(([key, obj]) => {
        const isImportance5 = obj && typeof obj === 'object' && obj.importance === 5;
        console.log(`[Step2] Field "${key}" importance: ${obj?.importance}, isImportance5: ${isImportance5}`);
        return isImportance5;
      })
      .map(([key, obj]) => ({
        key,
        type: obj.type || 'string',
        label: obj.label || obj.description || key,
        description: obj.description || '',
        default: obj.default,
      }));
    console.log('[Step2] importance5Fields:', fields);
    return fields;
  }, [selectedCosyflow]);

  const hasImportance5Fields = importance5Fields.length > 0;
  console.log('[Step2] hasImportance5Fields:', hasImportance5Fields);


  const handleImageUpload = (e, fieldName) => {
    const file = e.target.files?.[0];
    if (file) {
      setWizardData(prev => ({
        ...prev,
        uploadedImages: { ...prev.uploadedImages, [fieldName]: file },
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setWizardData(prev => ({
          ...prev,
          imagePreviews: { ...prev.imagePreviews, [fieldName]: reader.result },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = (fieldName) => {
    setWizardData(prev => {
      const newUploadedImages = { ...prev.uploadedImages };
      const newImagePreviews = { ...prev.imagePreviews };
      delete newUploadedImages[fieldName];
      delete newImagePreviews[fieldName];
      return { ...prev, uploadedImages: newUploadedImages, imagePreviews: newImagePreviews };
    });
    if (fileInputRefs.current[fieldName]) {
      fileInputRefs.current[fieldName].value = '';
    }
  };

  const toggleItemSelection = (itemId) => {
    setWizardData(prev => {
      const isSelected = prev.selectedItemIds.includes(itemId);
      return {
        ...prev,
        selectedItemIds: isSelected
          ? prev.selectedItemIds.filter(id => id !== itemId)
          : [...prev.selectedItemIds, itemId],
      };
    });
  };

  const handleGallerySelect = (imgObj) => {
    const mediaUrl = typeof imgObj === 'string' ? imgObj : (imgObj?.url || imgObj?.image);
    if (!mediaUrl) return;
    setWizardData(prev => {
      const existingItem = prev.generatedItems.find(item => item.imageUrl === mediaUrl);
      if (existingItem) {
        if (prev.selectedItemIds.includes(existingItem.id)) {
          return prev;
        }
        return {
          ...prev,
          selectedItemIds: [...prev.selectedItemIds, existingItem.id],
        };
      }

      const detectedFromContent = detectMediaType(mediaUrl);
      const mediaType = detectedFromContent !== 'image' ? detectedFromContent : modelOutputType;
      const newItem = {
        id: `gallery-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
        prompt: 'Selected from gallery',
        imageUrl: mediaUrl,
        mediaType,
      };

      return {
        ...prev,
        generatedItems: [...prev.generatedItems, newItem],
        selectedItemIds: [...prev.selectedItemIds, newItem.id],
      };
    });
  };

  const renderUploadField = (fieldName, showLabel = true) => (
    <div key={fieldName} className="border border-white/10 rounded-lg p-3 bg-dark-blue/30">
      {showLabel && (
        <p className="text-xs text-white/50 mb-2">
          Field: <span className="text-cyan">{fieldName}</span>
        </p>
      )}
      {wizardData.imagePreviews[fieldName] ? (
        <div className="relative">
          <img src={wizardData.imagePreviews[fieldName]} alt={`Preview for ${fieldName}`} className="max-h-32 mx-auto rounded-lg" />
          <button onClick={() => handleRemoveImage(fieldName)} className="absolute top-1 right-1 bg-red-500/80 hover:bg-red-500 text-white rounded-full p-1 transition-colors">
            <Icons.Close />
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileInputRefs.current[fieldName]?.click()}
          className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center cursor-pointer hover:border-cyan/50 hover:bg-dark-blue/20 transition-all"
        >
          <div className="text-white/50 opacity-50 mb-2 flex justify-center"><Icons.Image /></div>
          <p className="text-sm text-white/50">Click to upload</p>
          <p className="text-xs text-white/40 mt-1">PNG, JPG up to 10MB</p>
        </div>
      )}
      <input
        ref={(ref) => { if (ref) fileInputRefs.current[fieldName] = ref; }}
        type="file"
        accept="image/*"
        onChange={(e) => handleImageUpload(e, fieldName)}
        className="hidden"
      />
    </div>
  );

  return (
    <div className="space-y-6">
      <p className="text-white/70">Generate content and select the outputs you want to use in your template gallery.</p>

      {/* Select from Gallery */}
      <div className="border border-white/20 rounded-xl p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h4 className="text-sm font-medium text-white">Choose from Gallery</h4>
            <p className="text-xs text-white/50 mt-1">
              Select images generated with this model from your gallery.
            </p>
          </div>
          <button
            onClick={() => setIsGalleryPickerOpen(true)}
            className="px-4 py-2.5 rounded-lg font-medium bg-cyan/20 hover:bg-cyan/30 text-white border border-cyan/50 transition-colors disabled:opacity-50"
            disabled={!wizardData.model || isGenerating}
          >
            Select from Gallery
          </button>
        </div>
      </div>

      {/* Dynamic Inputs Section - Show DynamicPromptForm if importance 5 fields exist */}
      {hasImportance5Fields && selectedCosyflow?.variables ? (
        <div className="border border-white/20 rounded-xl p-4 space-y-4">
          <div className="mb-2">
            <h4 className="text-sm font-medium text-white mb-1">Template Inputs</h4>
            {wizardData.basePrompt && (
              <p className="text-xs text-white/50">Base prompt: <span className="text-cyan">{wizardData.basePrompt}</span></p>
            )}
          </div>
          <DynamicPromptForm
            ref={dynamicFormRef}
            schema={selectedCosyflow.variables}
            initialValues={wizardData.fieldValues || {}}
            importanceFilter={5}
            isComfyUIModel={false}
            selectedModel={wizardData.model}
            onFieldChange={(key, value) => {
              setWizardData(prev => ({
                ...prev,
                fieldValues: { ...prev.fieldValues, [key]: value },
                galleryPrompt: key === 'prompt' ? value : prev.galleryPrompt,
              }));
            }}
            onB64Upload={(key, value) => {
              setWizardData(prev => ({
                ...prev,
                fieldValues: { ...prev.fieldValues, [key]: value },
                imagePreviews: { ...prev.imagePreviews, [key]: value },
              }));
            }}
          />
          <button
            onClick={handleValidatedGenerate}
            className="w-full px-4 py-2.5 rounded-lg font-medium bg-yellow text-dark-blue hover:bg-yellow/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            disabled={isGenerating}
          >
            {isGenerating ? (<>{ButtonSpinner} Generating... {progressDisplay}%</>) : (<><Icons.Plus /> Generate</>)}
          </button>
        </div>
      ) : (
        <>
          {/* Image Upload Section - Fallback when no importance 5 fields */}
          <div className="border border-white/20 rounded-xl p-4 space-y-4">
            {imageFieldNames.length > 1 ? (
              <button
                onClick={() => setIsImageUploadExpanded(!isImageUploadExpanded)}
                className="w-full flex items-center justify-between text-sm font-medium text-white bg-dark-blue/30 rounded-lg p-3 hover:bg-dark-blue/50 transition-all"
              >
                <span>Image Inputs ({imageFieldNames.length} fields) - Optional</span>
                <div className={`transition-transform duration-200 ${isImageUploadExpanded ? 'rotate-180' : ''}`}>
                  <Icons.ChevronDown />
                </div>
              </button>
            ) : (
              <label className="block text-sm font-medium text-white">Image Input (Optional)</label>
            )}
            {imageFieldNames.length === 0 && renderUploadField('default_image', false)}
            {imageFieldNames.length === 1 && <div className="space-y-3">{imageFieldNames.map(fieldName => renderUploadField(fieldName))}</div>}
            {imageFieldNames.length > 1 && (
              <div className={`overflow-hidden transition-all duration-300 ${isImageUploadExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="space-y-3 pt-2">{imageFieldNames.map(fieldName => renderUploadField(fieldName))}</div>
              </div>
            )}
          </div>

          {/* Prompt Input - Fallback when no importance 5 fields */}
          <div className="border border-white/20 rounded-xl p-4 space-y-3">
            <label className="block text-sm font-medium text-white">Prompt</label>
            {wizardData.basePrompt && (
              <p className="text-xs text-white/50 mb-2">Base prompt: <span className="text-cyan">{wizardData.basePrompt}</span></p>
            )}
            <textarea
              value={wizardData.galleryPrompt}
              onChange={(e) => setWizardData(prev => ({ ...prev, galleryPrompt: e.target.value }))}
              placeholder="Enter a prompt to generate gallery content..."
              rows={3}
              className={`w-full px-3 py-2.5 rounded-lg resize-none bg-dark-blue/50 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan/50 ${errors.galleryPrompt ? 'border-red-400' : ''}`}
              disabled={isGenerating}
            />
            {errors.galleryPrompt && <p className="text-sm text-red-400">{errors.galleryPrompt}</p>}
            <button
              onClick={onGenerateContent}
              className="w-full px-4 py-2.5 rounded-lg font-medium bg-yellow text-dark-blue hover:bg-yellow/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
              disabled={isGenerating || !wizardData.galleryPrompt.trim()}
            >
              {isGenerating ? (<>{ButtonSpinner} Generating... {progressDisplay}%</>) : (<><Icons.Plus /> Generate</>)}
            </button>
          </div>
        </>
      )}

      {/* Generated Items Grid */}
      {wizardData.generatedItems.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-white">Available Outputs</h4>
            <span className="text-sm text-yellow">{wizardData.selectedItemIds.length} selected</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {wizardData.generatedItems.map((item, index) => {
              const isSelected = wizardData.selectedItemIds.includes(item.id);
              const detectedFromContent = detectMediaType(item.imageUrl);
              const mediaType = detectedFromContent !== 'image' ? detectedFromContent : (item.mediaType || modelOutputType);
              return (
                <div
                  key={item.id}
                  onClick={() => toggleItemSelection(item.id)}
                  className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${isSelected ? 'border-yellow ring-2 ring-yellow/30' : 'border-white/20 hover:border-white/40'}`}
                >
                  <div className="aspect-square relative">
                    <MediaRenderer url={item.imageUrl} alt={item.prompt} className="w-full h-full" mediaType={mediaType} />
                    {mediaType !== 'image' && <div className="absolute top-2 left-2 z-10"><MediaTypeBadge type={mediaType} /></div>}
                    <div className={`absolute inset-0 transition-opacity pointer-events-none ${isSelected ? 'bg-yellow/20' : 'bg-transparent'}`} />
                    <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isSelected ? 'bg-yellow text-dark-blue' : 'bg-black/60 text-white/50 border border-white/20'}`}>
                      {isSelected ? <Icons.Check /> : <span className="text-xs">{index + 1}</span>}
                    </div>
                  </div>
                  <div className="p-2 bg-dark-blue/50"><p className="text-xs text-white/70 line-clamp-1">{item.prompt}</p></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {wizardData.generatedItems.length === 0 && (
        <div className="text-center py-8 text-white/50">
          <div className="opacity-50 mb-3 flex justify-center"><Icons.Image /></div>
          <p>No outputs yet. Generate some content above!</p>
        </div>
      )}

      <p className="text-center text-sm text-white/50">Click on outputs to select them for your gallery.</p>

      <ModelGalleryPicker
        isOpen={isGalleryPickerOpen}
        onClose={() => setIsGalleryPickerOpen(false)}
        modelName={wizardData.model}
        onSelect={handleGallerySelect}
        selectedMedia={wizardData.generatedItems
          .filter(item => wizardData.selectedItemIds.includes(item.id))
          .map(item => item.imageUrl)}
        title="Select from Gallery"
        subtitle="Media generated with this model"
      />
    </div>
  );
}

// ============================================
// Step 3: Details
// ============================================

export function Step3Details({
  wizardData,
  setWizardData,
  errors,
  setErrors,
  categories,
  allowNewCategories,
  isAddingNewCategory,
  setIsAddingNewCategory,
  newCategoryName,
  setNewCategoryName,
  isSubmitting,
  usernamePrefix = '',
}) {
  // Calculate the prefix with slash if username exists
  const titlePrefix = usernamePrefix ? `${usernamePrefix}/` : '';

  // Get the editable part of the title (everything after the prefix)
  const editableTitle = wizardData.title.startsWith(titlePrefix)
    ? wizardData.title.slice(titlePrefix.length)
    : wizardData.title;

  // Handle title change - always prepend the prefix
  const handleTitleChange = (e) => {
    const newEditableTitle = e.target.value;
    setWizardData(prev => ({ ...prev, title: titlePrefix + newEditableTitle }));
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-white mb-2">Title <span className="text-red-400">*</span></label>
        <div className="flex">
          {titlePrefix && (
            <div className="flex items-center px-3 py-2.5 rounded-l-lg bg-dark-blue/80 border border-r-0 border-white/30 text-cyan font-medium select-none">
              {titlePrefix}
            </div>
          )}
          <input
            type="text"
            value={editableTitle}
            onChange={handleTitleChange}
            placeholder="Enter template title"
            className={`flex-1 px-3 py-2.5 ${titlePrefix ? 'rounded-r-lg' : 'rounded-lg'} bg-dark-blue/50 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan/50 ${errors.title ? 'border-red-400' : ''}`}
            disabled={isSubmitting}
          />
        </div>
        {errors.title && <p className="text-sm text-red-400 mt-1">{errors.title}</p>}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-white mb-2">Description <span className="text-red-400">*</span></label>
        <textarea
          value={wizardData.description}
          onChange={(e) => setWizardData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe what this template does..."
          rows={3}
          className={`w-full px-3 py-2.5 rounded-lg resize-none bg-dark-blue/50 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan/50 ${errors.description ? 'border-red-400' : ''}`}
          disabled={isSubmitting}
        />
        {errors.description && <p className="text-sm text-red-400 mt-1">{errors.description}</p>}
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-white mb-2">Use Case <span className="text-red-400">*</span></label>
        {!isAddingNewCategory ? (
          <div className="space-y-2">
            <SearchableUseCaseDropdown
              value={wizardData.category}
              options={categories}
              disabled={isSubmitting}
              hasError={!!errors.category}
              onChange={(value) => {
                setWizardData(prev => ({ ...prev, category: value }));
                if (errors.category) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.category;
                    return newErrors;
                  });
                }
              }}
            />
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              type="text"
              value={newCategoryName}
              placeholder="Enter new use case name"
              className="flex-1 px-3 py-2.5 rounded-lg bg-dark-blue/50 border border-white/20 text-white"
              onChange={(e) => setNewCategoryName(e.target.value)}
              onBlur={() => { if (newCategoryName.trim()) setWizardData(prev => ({ ...prev, category: newCategoryName.trim() })); }}
              autoFocus
              disabled={isSubmitting}
            />
            <button type="button" onClick={() => { setIsAddingNewCategory(false); setNewCategoryName(''); }} className="px-3 py-2 text-white/70 hover:text-white hover:bg-dark-blue/50 rounded-lg transition-colors" disabled={isSubmitting}>
              <Icons.Close />
            </button>
          </div>
        )}
        {wizardData.category && isAddingNewCategory && <p className="text-xs text-cyan mt-1">New use case: {wizardData.category}</p>}
        {errors.category && <p className="text-sm text-red-400 mt-1">{errors.category}</p>}
      </div>
    </div>
  );
}

// ============================================
// Step 4: Preview
// ============================================

export function Step4Preview({ wizardData, selectedItems, modelOutputType }) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-white mb-2">Review Your Template</h3>
        <p className="text-white/70">Review all details before creating your template.</p>
      </div>

      {/* Template Card Preview */}
      <div className="bg-dark-blue/50 rounded-xl p-4 border border-white/20">
        <h4 className="text-sm font-medium text-white/50 mb-3">Template Card Preview</h4>
        <div className="flex gap-4">
          <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 relative">
            {(() => {
              const firstItem = selectedItems[0];
              if (!firstItem?.imageUrl) {
                return <div className="w-full h-full bg-dark-blue flex items-center justify-center"><Icons.Image /></div>;
              }
              const detectedFromContent = detectMediaType(firstItem.imageUrl);
              const mediaType = detectedFromContent !== 'image' ? detectedFromContent : (firstItem.mediaType || modelOutputType);
              return (
                <>
                  <MediaRenderer url={firstItem.imageUrl} alt="Preview" className="w-full h-full" mediaType={mediaType} />
                  {mediaType !== 'image' && <div className="absolute top-1 left-1"><MediaTypeBadge type={mediaType} /></div>}
                </>
              );
            })()}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-white truncate">{wizardData.title}</h4>
            <p className="text-sm text-white/70 line-clamp-2">{wizardData.description}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs px-2 py-1 bg-yellow/20 text-yellow rounded-full">{wizardData.category}</span>
              <span className="text-xs text-white/50">{wizardData.model}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Content */}
      <div>
        <h4 className="text-sm font-medium text-white mb-3">Gallery Content ({selectedItems.length})</h4>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {selectedItems.map((item, index) => {
            const detectedFromContent = detectMediaType(item.imageUrl);
            const mediaType = detectedFromContent !== 'image' ? detectedFromContent : (item.mediaType || modelOutputType);
            return (
              <div key={item.id} className="aspect-square rounded-lg overflow-hidden border border-white/20 relative">
                <MediaRenderer url={item.imageUrl} alt={`Gallery ${index + 1}`} className="w-full h-full" mediaType={mediaType} />
                {mediaType !== 'image' && <div className="absolute top-1 left-1"><MediaTypeBadge type={mediaType} /></div>}
                <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-yellow text-dark-blue flex items-center justify-center text-xs font-semibold">{index + 1}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Template Details */}
      <div className="bg-dark-blue/50 rounded-xl p-4 space-y-3">
        <h4 className="text-sm font-medium text-white">Template Details</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div><span className="text-white/50">Title:</span><p className="text-white font-medium">{wizardData.title}</p></div>
          <div><span className="text-white/50">Category:</span><p className="text-white font-medium">{wizardData.category}</p></div>
          <div><span className="text-white/50">Model:</span><p className="text-white font-medium">{wizardData.model}</p></div>
          <div><span className="text-white/50">Gallery Items:</span><p className="text-white font-medium">{selectedItems.length}</p></div>
        </div>
        <div><span className="text-white/50 text-sm">Description:</span><p className="text-white text-sm mt-1">{wizardData.description}</p></div>
        {wizardData.basePrompt && <div><span className="text-white/50 text-sm">Base Prompt:</span><p className="text-white text-sm mt-1">{wizardData.basePrompt}</p></div>}
      </div>
    </div>
  );
}
